import { supabase } from '@/integrations/supabase/client';
import { SmartGoal, MiniGoal } from '@/types';
import { handleDatabaseError, handleAuthError } from '@/lib/errorHandler';
import { createGoalSchema } from '@/lib/validations';

export const goalsService = {
  async createGoal(goal: Omit<SmartGoal, 'id' | 'userId' | 'createdAt'>) {
    console.log('📝 Iniciando criação de meta:', { title: goal.title, pillar: goal.pillar });
    
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) {
        console.error('❌ Usuário não autenticado');
        handleAuthError(new Error('Usuário não autenticado'));
        throw new Error('Usuário não autenticado');
      }

      console.log('✅ Usuário autenticado:', user.id);

      // Validate input
      try {
        createGoalSchema.parse({
          title: goal.title,
          specific: goal.specific,
          measurable: goal.measurable,
          achievable: goal.achievable,
          relevant: goal.relevant,
          timeBoundISO: goal.timeBoundISO
        });
        console.log('✅ Validação de schema passou');
      } catch (validationError) {
        console.error('❌ Erro de validação:', validationError);
        throw validationError;
      }

    const goalPayload = {
      user_id: user.id,
      title: goal.title,
      pilar: goal.pillar,
      pillars: goal.pillars || [goal.pillar], // ✅ Suporte a múltiplos pilares
      status: goal.status,
      deadline: goal.timeBoundISO,
      specific: goal.specific,
      measurable: goal.measurable,
      achievable: goal.achievable,
      relevant: goal.relevant,
      time_bound: goal.timeBoundISO
    };

      console.log('📤 Inserindo meta no Supabase:', goalPayload);

      const { data: goalData, error: goalError } = await supabase
        .from('user_goals')
        .insert(goalPayload)
        .select()
        .single();

      if (goalError) {
        console.error('❌ Erro ao inserir meta no Supabase:', goalError);
        handleDatabaseError(goalError);
        throw goalError;
      }

      console.log('✅ Meta criada com sucesso, ID:', goalData.id);

      // Criar mini-metas
      if (goal.miniGoals && goal.miniGoals.length > 0) {
        console.log('📝 Criando', goal.miniGoals.length, 'mini-metas...');
        
        const { error: miniError } = await supabase
          .from('user_mini_goals')
          .insert(
            goal.miniGoals.map(mini => ({
              goal_id: goalData.id,
              user_id: user.id,
              title: mini.title,
              completed: mini.completed
            }))
          );

        if (miniError) {
          console.error('❌ Erro ao criar mini-metas:', miniError);
          handleDatabaseError(miniError);
          throw miniError;
        }

        console.log('✅ Mini-metas criadas com sucesso');
      }

      console.log('🎉 Meta completa criada com sucesso!');
      return goalData.id;
    } catch (error) {
      console.error('❌ Erro final na criação de meta:', error);
      
      if (error instanceof Error && error.message.includes('autenticado')) {
        throw error;
      }
      handleDatabaseError(error);
      throw error;
    }
  },

  async updateGoal(goalId: string, updates: Partial<SmartGoal>) {
    const { data: { user } } = await supabase.auth.getUser();
    if (!user) throw new Error('Usuário não autenticado');

    // ⚡ VALIDAR updates
    if (updates.title && updates.title.trim().length < 3) {
      throw new Error('Título deve ter no mínimo 3 caracteres');
    }
    
    if (updates.timeBoundISO) {
      const deadline = new Date(updates.timeBoundISO);
      const now = new Date();
      now.setHours(0, 0, 0, 0);
      if (deadline < now) {
        throw new Error('Prazo não pode ser no passado');
      }
    }

    const updateData: any = {};
    if (updates.title) updateData.title = updates.title.trim();
    if (updates.status) updateData.status = updates.status;
    if (updates.timeBoundISO) updateData.deadline = updates.timeBoundISO;
    if (updates.specific) updateData.specific = updates.specific;
    if (updates.measurable) updateData.measurable = updates.measurable;
    if (updates.achievable) updateData.achievable = updates.achievable;
    if (updates.relevant) updateData.relevant = updates.relevant;
    if (updates.timeBoundISO) updateData.time_bound = updates.timeBoundISO;

    const { error } = await supabase
      .from('user_goals')
      .update(updateData)
      .eq('id', goalId)
      .eq('user_id', user.id);

    if (error) throw error;
  },

  async deleteGoal(goalId: string) {
    const { data: { user } } = await supabase.auth.getUser();
    if (!user) throw new Error('Usuário não autenticado');

    const { error } = await supabase
      .from('user_goals')
      .delete()
      .eq('id', goalId)
      .eq('user_id', user.id);

    if (error) throw error;
  },

  async addMiniGoal(goalId: string, miniGoal: Omit<MiniGoal, 'id' | 'parentGoalId'>) {
    const { data: { user } } = await supabase.auth.getUser();
    if (!user) throw new Error('Usuário não autenticado');

    const { data, error } = await supabase
      .from('user_mini_goals')
      .insert({
        goal_id: goalId,
        user_id: user.id,
        title: miniGoal.title,
        completed: miniGoal.completed
      })
      .select()
      .single();

    if (error) throw error;
    return data.id;
  },

  async toggleMiniGoal(miniGoalId: string, completed: boolean) {
    console.log('📝 toggleMiniGoal called:', { miniGoalId, completed });
    
    const { data: { user } } = await supabase.auth.getUser();
    if (!user) {
      console.error('❌ User not authenticated');
      throw new Error('Usuário não autenticado');
    }

    console.log('✅ User authenticated:', user.id);
    console.log('📤 Updating mini-goal in database...');

    const { error } = await supabase
      .from('user_mini_goals')
      .update({ completed })
      .eq('id', miniGoalId)
      .eq('user_id', user.id);

    if (error) {
      console.error('❌ Database update failed:', error);
      throw error;
    }

    console.log('✅ Mini-goal updated successfully in database');
  },

  async deleteMiniGoal(miniGoalId: string) {
    const { data: { user } } = await supabase.auth.getUser();
    if (!user) throw new Error('Usuário não autenticado');

    const { error } = await supabase
      .from('user_mini_goals')
      .delete()
      .eq('id', miniGoalId)
      .eq('user_id', user.id);

    if (error) throw error;
  }
};
